import { dramas } from '@/mocks/dramas';
import type { Drama } from '@/types/drama';

export function getDramaById(id: string | undefined): Drama | undefined {
  if (!id) return undefined;
  return dramas.find((d) => d.id === id);
}

export function searchDramas(keyword: string): Drama[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return dramas.filter((d) => {
    const haystack = [d.title, d.region, d.director, ...d.actors, ...d.genres, ...d.tags]
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  });
}

export interface DramaFilter {
  genre?: string;
  region?: string;
  year?: number | '';
  keyword?: string;
}

export function filterDramas(filter: DramaFilter): Drama[] {
  return dramas.filter((d) => {
    if (filter.genre && !d.genres.includes(filter.genre)) return false;
    if (filter.region && d.region !== filter.region) return false;
    if (filter.year && d.year !== filter.year) return false;
    if (filter.keyword) {
      const q = filter.keyword.trim().toLowerCase();
      const haystack = [d.title, ...d.actors].join(' ').toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
}

export type SortKey = 'update' | 'new' | 'hot';

export function sortDramas(list: Drama[], sort: SortKey): Drama[] {
  const copy = [...list];
  if (sort === 'hot') {
    copy.sort((a, b) => b.hot - a.hot);
  } else if (sort === 'new') {
    copy.sort((a, b) => b.year - a.year || b.hot - a.hot);
  } else {
    copy.sort((a, b) => b.updateTime.localeCompare(a.updateTime));
  }
  return copy;
}

export function getRelatedDramas(drama: Drama, limit = 4): Drama[] {
  return dramas
    .filter((d) => d.id !== drama.id)
    .map((d) => {
      const genreScore = d.genres.filter((g) => drama.genres.includes(g)).length * 3;
      const tagScore = d.tags.filter((t) => drama.tags.includes(t)).length * 2;
      const regionScore = d.region === drama.region ? 1 : 0;
      return { drama: d, score: genreScore + tagScore + regionScore };
    })
    .sort((a, b) => b.score - a.score || b.drama.hot - a.drama.hot)
    .slice(0, limit)
    .map((item) => item.drama);
}

export function getLatestUpdates(limit = 8): Drama[] {
  return sortDramas(dramas, 'update').slice(0, limit);
}

export function getHotPicks(limit = 6): Drama[] {
  return sortDramas(dramas, 'hot').slice(0, limit);
}

export function getFeatured(): Drama[] {
  return dramas.filter((d) => d.featured);
}